import { type Time, TickMarkType } from "lightweight-charts"
import { DISPLAY_TIME_ZONE } from "../../ui/displayTime.ts"

// Bars keep their real UTC timestamps; only the labels are rendered, in Indian
// time for every venue (NSE opens 09:15, New York 19:00 or 20:00, Tokyo 05:30).
// The chart places day ticks where the UTC date changes (05:30 IST); for venues
// that close overnight that is the first bar of each session.

const formatter = (timeZone: string, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat("en-GB", { timeZone, ...options })

const toDate = (time: Time) => new Date((typeof time === "number" ? time : 0) * 1000)

export interface TimeFormatters {
  /** Crosshair label. */
  readonly timeFormatter: (time: Time) => string
  /** Axis labels. */
  readonly tickMarkFormatter: (time: Time, type: TickMarkType) => string
}

export const timeFormatters = (intraday: boolean): TimeFormatters => {
  const timeZone = DISPLAY_TIME_ZONE
  const year = formatter(timeZone, { year: "numeric" })
  const month = formatter(timeZone, { month: "short" })
  const day = formatter(timeZone, { day: "numeric", month: "short" })
  const clock = formatter(timeZone, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
  const full = formatter(timeZone, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "2-digit",
    ...(intraday ? { hour: "2-digit", minute: "2-digit", hourCycle: "h23" } : {})
  })

  return {
    timeFormatter: (time) => full.format(toDate(time)),
    tickMarkFormatter: (time, type) => {
      const date = toDate(time)
      switch (type) {
        case TickMarkType.Year:
          return year.format(date)
        case TickMarkType.Month:
          return month.format(date)
        case TickMarkType.DayOfMonth:
          return day.format(date)
        case TickMarkType.Time:
        case TickMarkType.TimeWithSeconds:
          return clock.format(date)
      }
    }
  }
}
